import { IFieldDef, IGetValueArgs } from "@components/smart/FieldInfo";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrExtraSalaryEntity,
    PrExtraSalaryEntity,
    PrExtraSalaryTemporalEntity,
    PrExtraSalaryTypeEntity
} from "@odata/GeneratedEntityTypes";
import { PrExtraSalaryOriginCode } from "@odata/GeneratedEnums";
import { getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { transformToODataString } from "@odata/OData.utils";
import {
    getTableTemporalAdditionalProperties,
    getTemporalAdditionalProperties,
    getTemporalDataColumns,
    getTemporalPropertyFieldDefinition,
    TemporalEntityProp,
    TemporalValidityFieldsDef
} from "@odata/TemporalUtils";
import { isNotDefined } from "@utils/general";
import i18next from "i18next";

import { GroupedField, Sort, ValueType } from "../../../enums";
import { Model } from "../../../model/Model";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef, ITableDefTabData, SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import {
    extraSalariesTypeFormatter,
    ExtraSalaryTab,
    extraSalaryValueFormatter,
    ExtraSalaryVariant,
    FakeTypePath,
    getExtraSalaryColumns,
    getExtraSalaryFieldDefinition,
    getExtraSalaryTemporalDialogSettings,
    getFilterForTabId
} from "./ExtraSalary.utils";
import ExtraSalaryFormView from "./ExtraSalaryFormView";

interface IGetDefinitionArgs {
    inEditableWindow?: boolean;
    withoutLegallyRequired?: boolean;
    selectedIds?: number[];
    filters?: string[];
}

function isCustom(args: IGetValueArgs): boolean {
    const entity = args.storage.data.entity as IPrExtraSalaryEntity;
    return entity?.Type?.OriginCode === PrExtraSalaryOriginCode.Custom;
}

function isLegislative(args: IGetValueArgs): boolean {
    const entity = args.storage.data.entity as IPrExtraSalaryEntity;
    return entity?.Type?.OriginCode === PrExtraSalaryOriginCode.Legislative;
}

export const getDefinitionsFactory: ((args?: IGetDefinitionArgs) => IGetDefinition) = (args) => {
    const { inEditableWindow, selectedIds, filters, withoutLegallyRequired } = args ?? {};

    const getDefinitions: IGetDefinition = (): IDefinition => {

        const preventStoreVariant = inEditableWindow;

        const _filters: string[] = [...(filters ?? [])];
        if (selectedIds?.length) {
            _filters.push(`not(${PrExtraSalaryEntity.Id} in (${transformToODataString(selectedIds, ValueType.Number)}))`);
        }

        if (withoutLegallyRequired) {
            _filters.push(`not ${PrExtraSalaryEntity.Type}/${PrExtraSalaryTypeEntity.OriginCode} eq '${PrExtraSalaryOriginCode.Legislative}'`);
        }

        if (inEditableWindow) {
            _filters.push(getFilterForTabId(ExtraSalaryTab.Active));
        }

        const tableAdditionalProperties: IFieldDef[] = [];

        if (inEditableWindow) {
            const cols = getTemporalDataColumns(getExtraSalaryColumns(ExtraSalaryVariant.Employment));

            cols.forEach(col => {
                tableAdditionalProperties.push({ id: createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, col) });
                tableAdditionalProperties.push({ id: createPath(PrExtraSalaryEntity.TemporalPropertyBag, col) });
            });
        } else {
            tableAdditionalProperties.push(...getTableTemporalAdditionalProperties(PrExtraSalaryEntity));
        }

        const defaultTabs: ITableDefTabData[] = [
            { id: ExtraSalaryTab.Active, title: i18next.t("ExtraSalary:Tabs.Active") },
            { id: ExtraSalaryTab.Inactive, title: i18next.t("ExtraSalary:Tabs.Inactive") },
            { id: SECONDARY_FILTERS_ALL, title: i18next.t("ExtraSalary:Tabs.All") }
        ];
        const tabs: ITableDefTabData[] = inEditableWindow ? null : defaultTabs;

        const table: ISplitPageTableDef = {
            id: `${EntityTypeName.PrExtraSalary}Table`,
            preventStoreVariant,
            filterBarDef: [{
                ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
                defaultFilters: [
                    createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Name),
                    createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.OriginCode),
                    createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Value),
                    createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.ComputationType)
                ],
                filterDefinition: {
                    [createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.OriginCode)]: {
                        label: i18next.t("ExtraSalary:Form.Type"),
                        formatter: extraSalariesTypeFormatter
                    },
                    [createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Name)]: {},
                    [createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Value)]: {},
                    [createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.ComputationType)]: {
                        fieldSettings: {
                            displayName: "Name"
                        }
                    }
                },
                isValueHelp: true
            }],
            filter: _filters.join(" AND "),
            initialSortBy: [
                { id: createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.Origin), sort: Sort.Asc },
                {
                    id: createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Name),
                    sort: Sort.Asc
                }
            ],
            columns: [
                createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Name),
                createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.Origin),
                createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Value),
                createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.ComputationType)
            ],
            columnDefinition: {
                [createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Name)]: {},
                [createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Value)]: {
                    formatter: extraSalaryValueFormatter
                },
                [createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.ComputationType)]: {
                    fieldSettings: {
                        displayName: "Name"
                    }
                },
                [createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.Origin)]: {
                    formatter: extraSalariesTypeFormatter
                }
            },
            title: i18next.t("ExtraSalary:Title"),
            additionalProperties: tableAdditionalProperties,
            tabs,
            tabsSettings: {
                customFilter: getFilterForTabId
            }
        };

        if (withoutLegallyRequired) {
            // remove type filter as it doesn't make sense in this context
            table.filterBarDef[0].defaultFilters = table.filterBarDef[0].defaultFilters.filter(id => id !== createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.OriginCode));
            delete table.filterBarDef[0].filterDefinition[createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.OriginCode)];
        }

        const summary: ISummaryItem[] = [];
        const temporalDialogSettings = getExtraSalaryTemporalDialogSettings(ExtraSalaryVariant.Base);

        const tmpFieldsDef = { ...TemporalValidityFieldsDef };

        tmpFieldsDef["TemporalPropertyBag/DateValidFrom"].isReadOnly = (args: IGetValueArgs) => {
            const custom = isCustom(args);
            const formStorage = args.storage as FormStorage;

            // called from multiple different context in SmartFastEntryList (for both field and column itself)
            // return when parent doesn't have key => meaning its column check, so that we don't disable the whole column
            // todo better solution/check
            //  ?? probably part of isTemporalFieldReadOnly ??
            if (custom || isNotDefined(args.bindingContext.getParent().getKey())) {
                return false;
            }

            const isSaved = !!args.storage.getValue(args.bindingContext.getParent(), { dataStore: formStorage.data.origEntity });

            return isSaved;
        };
        tmpFieldsDef["TemporalPropertyBag/DateValidTo"].isDisabled = isLegislative;

        const form: IFormDef = {
            id: `${EntityTypeName.PrExtraSalary}Form`,
            translationFiles: getDefinitions.translationFiles,
            getItemBreadCrumbText: (storage: Model) =>
                    getItemBreadCrumbsText(storage, i18next.t("ExtraSalary:New"), storage.data.entity?.CurrentTemporalPropertyBag?.Name),
            isDeletable: true,
            formControl: ExtraSalaryFormView,
            summary,
            additionalProperties: [
                ...getTemporalAdditionalProperties(PrExtraSalaryTemporalEntity, PrExtraSalaryEntity),
                {
                    id: PrExtraSalaryEntity.Type,
                    additionalProperties: [
                        { id: PrExtraSalaryTypeEntity.Code },
                        { id: PrExtraSalaryTypeEntity.OriginCode }
                    ]
                }
            ],
            fieldDefinition: {
                [createPath(TemporalEntityProp.TemporalPropertyBag, FakeTypePath)]: {
                    ...getExtraSalaryFieldDefinition(FakeTypePath)
                },
                ...getTemporalPropertyFieldDefinition({
                    propName: PrExtraSalaryTemporalEntity.Name,
                    definition: {
                        ...getExtraSalaryFieldDefinition(PrExtraSalaryTemporalEntity.Name, temporalDialogSettings),
                        groupedField: GroupedField.WrappedMultiStart,
                    },
                    withOpeningButton: false
                }),
                ...getTemporalPropertyFieldDefinition({
                    propName: PrExtraSalaryTemporalEntity.ComputationType,
                    definition: {
                        ...getExtraSalaryFieldDefinition(PrExtraSalaryTemporalEntity.ComputationType, temporalDialogSettings)
                    },
                    withOpeningButton: false
                }),
                [createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.Origin)]: {
                    ...getExtraSalaryFieldDefinition(createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.Origin))
                },
                ...getTemporalPropertyFieldDefinition({
                    propName: PrExtraSalaryTemporalEntity.Value,
                    definition: {
                        ...getExtraSalaryFieldDefinition(PrExtraSalaryTemporalEntity.Value, temporalDialogSettings),
                        groupedField: GroupedField.WrappedMultiEnd
                    }
                }),
                ...tmpFieldsDef
            },
            groups: [
                {
                    id: "main",
                    rows: [
                        [
                            { id: createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Name) },
                            { id: createPath(PrExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.Origin) },
                            { id: createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.ComputationType) },
                            { id: createPath(PrExtraSalaryEntity.CurrentTemporalPropertyBag, PrExtraSalaryTemporalEntity.Value) }
                        ]
                    ]
                }
            ]
        };
        return {
            entitySet: EntitySetName.PrExtraSalaries,
            table,
            form
        };
    };
    getDefinitions.translationFiles = ["ExtraSalary", "Common", getEnumNameSpaceName(EntityTypeName.PrExtraSalaryOrigin)];

    return getDefinitions;
};

export const getDefinitions = getDefinitionsFactory();

setDefByEntityType(EntityTypeName.PrExtraSalary, getDefinitions);